<script setup lang="ts">
import {computed, nextTick, onMounted, onUnmounted, useTemplateRef, watch} from "vue";
import {useStorage} from "@vueuse/core";
import WordCloud from "wordcloud";

const props = defineProps<{
    question: any;
    hasFilters?: boolean;
}>();

const darkMode = useStorage(
    "color-scheme",
    window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light",
);

const words = computed(() => {
    const allWords = props.question.responses.flatMap((A) => A.data);
    const wordCounts = allWords.reduce((acc, word) => {
        acc[word] = (acc[word] || 0) + 1;
        return acc;
    }, {});
    return Object.entries(wordCounts);
});

const wordCloudContainer = useTemplateRef("wordCloudContainer");

const getWeightFactor = () => {
    if (!wordCloudContainer.value) {
        return 30;
    }

    const width = wordCloudContainer.value.offsetWidth;

    return Math.max(8, Math.min(30, width / 15));
};

const initWordCloud = async () => {
    await nextTick();
    if (!words.value?.length || !wordCloudContainer.value) {
        return;
    }
    try {
        WordCloud(wordCloudContainer.value, {
            list: words.value,
            weightFactor: getWeightFactor(),
            fontFamily: "Poppins",
            fontWeight: 500,
            color: darkMode.value === "dark" ? "random-light" : "random-dark",
            backgroundColor: "transparent",
            drawOutOfBound: false,
            shrinkToFit: true,
        });
    } catch (e) {
        console.log(e);
    }
};

let resizeTimeout: number | null = null;
const handleResize = () => {
    if (resizeTimeout) {
        clearTimeout(resizeTimeout);
    }
    resizeTimeout = window.setTimeout(initWordCloud, 200);
};

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
    initWordCloud();
    if (wordCloudContainer.value) {
        resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(wordCloudContainer.value);
    }
});

onUnmounted(() => {
    if (resizeObserver) {
        resizeObserver.disconnect();
    }
    if (resizeTimeout) {
        clearTimeout(resizeTimeout);
    }
});

watch([words, darkMode], initWordCloud, {deep: true});
</script>
<template>
    <div>
        <div v-if="!words.length" class="w-full p-8">
            <p class="text-center text-stone-500">
                {{ hasFilters ? "No responses found for these filters." : "No responses yet." }}
            </p>
        </div>
        <div
            v-else
            ref="wordCloudContainer"
            class="aspect-square overflow-hidden rounded-lg bg-stone-50 dark:bg-stone-900"
        ></div>
    </div>
</template>
<style scoped></style>
